import { Input, makeStyles, SearchBox } from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import { ColumnDirective, ColumnsDirective, Freeze, Inject } from '@syncfusion/ej2-react-grids';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFeatureEnabled } from '../../../api/features/features';
import { ICalculationOptions, IWhitePaperColumn } from '../../../model';
import { EditableOptionsPanelEnabled } from '../../../utils/FeatureFlags.constants';
import { DefaultTooltipHostV9 } from '../../common/DefaultTooltipHostV9';
import { StyledTable } from '../StyledTable';

const useEditableCellStyles = makeStyles({
    root: {
        width: '60px',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    input: {
        height: '32px',
        textAlign: 'center',
        paddingTop: '4px',
        paddingBottom: '4px',
        paddingLeft: '4px',
        paddingRight: '4px',
    },
});

// Separate component for editable cell with internal state to prevent parent re-renders
const EditableCell = React.memo(
    ({
        initialValue,
        onChange,
        ariaLabel,
    }: {
        initialValue: string;
        onChange: (newValue: string) => void;
        ariaLabel: string;
        rowNumber: string;
        columnId: number;
    }) => {
        const styles = useEditableCellStyles();
        const [localValue, setLocalValue] = useState(initialValue);

        // Update local value when initial value changes from parent
        useEffect(() => {
            setLocalValue(initialValue);
        }, [initialValue]);

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
            e?.stopPropagation();
            const value = data.value || '';

            // Validate: only allow empty string or whole number in the inclusive range of 0 to 99
            if (!/^$|^[0-9]$|^[1-9][0-9]$/.test(value)) {
                return;
            }

            setLocalValue(value);
            onChange(value);
        };

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            e.stopPropagation();
            // Select all text when focused for easy editing
            e.target.select();
        };

        return (
            <Input
                value={localValue}
                onChange={handleChange}
                onClick={(e) => e.stopPropagation()}
                onFocus={handleFocus}
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                maxLength={2}
                className={styles.root}
                input={{ className: styles.input }}
                aria-label={ariaLabel}
            />
        );
    },
    (prevProps, nextProps) => {
        // Only re-render if the initial value actually changes or if it's a different cell
        return (
            prevProps.initialValue === nextProps.initialValue &&
            prevProps.rowNumber === nextProps.rowNumber &&
            prevProps.columnId === nextProps.columnId
        );
    }
);

EditableCell.displayName = 'EditableCell';

const useSearchBoxStyles = makeStyles({
    root: {
        width: '250px',
        minWidth: '250px',
        maxWidth: '250px',
    },
});

const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 250px)',
    minHeight: '300px',
    maxHeight: '75vh',
    overflow: 'hidden',
};

const searchContainerStyles: React.CSSProperties = {
    flexShrink: 0,
    paddingBottom: '8px',
};

const tableContainerStyles: React.CSSProperties = {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    minWidth: '35rem',
};

// CSS to remove cell selection border
const gridCustomStyles = `
    
    .calculation-options-grid .e-gridcontent .e-rowcell.e-focus,
    .calculation-options-grid .e-gridcontent .e-rowcell.e-focused,
    .calculation-options-grid .e-gridcontent .e-rowcell:focus {
        box-shadow: none !important;
        outline: none !important;
    }
`;

export interface ICalculationOptionsTableProps {
    calculationOptions: ICalculationOptions[];
    columns?: IWhitePaperColumn[] | null;
    onCellEdit?: (rowNumber: string | null, columnId: number, newValue: string) => void;
    isEditingEnabled?: boolean;
    onGridReady?: () => void;
}

export const CalculationOptionsTable = ({
    calculationOptions,
    columns,
    onCellEdit,
    isEditingEnabled,
    onGridReady,
}: ICalculationOptionsTableProps) => {
    const { t } = useTranslation();
    const searchBoxClasses = useSearchBoxStyles();
    const [isSearchboxFocused, { setTrue: setIsSearchboxFocusedTrue, setFalse: setIsSearchboxFocusedFalse }] = useBoolean(false);
    const { data: isSearchEnabled } = useIsFeatureEnabled({ featureFlagName: EditableOptionsPanelEnabled });
    const hasCalculationOptions = calculationOptions?.length;
    const gridRef = React.useRef<any>(null);
    const [searchValue, setSearchValue] = useState('');
    const searchPlaceholder = t('searchForOption').toString();

    const handleSearch = useCallback((newValue: string) => {
        setSearchValue(newValue);
        if (gridRef.current) {
            const contentElement = gridRef.current.element?.querySelector('.e-content');
            if (contentElement) {
                contentElement.scrollTop = 0;
            }
            gridRef.current.search(newValue);
        }
    }, []);

    const handleClearSearch = useCallback(() => {
        setSearchValue('');
        if (gridRef.current) {
            gridRef.current.search('');
        }
    }, []);

    const handleDataBound = useCallback(() => {
        // Notify parent that grid is ready with data
        if (onGridReady) {
            onGridReady();
        }
    }, [onGridReady]);

    const handleChange = useCallback(
        (rowNumber: string | null, columnId: number | undefined, newValue: string) => {
            if (!rowNumber || columnId === undefined || columnId === null) return;

            if (onCellEdit) {
                onCellEdit(rowNumber, columnId, newValue);
            }
        },
        [onCellEdit]
    );

    // Flatten the data so Syncfusion can display it
    const flattenedData = useMemo(() => {
        return calculationOptions.map((option) => {
            const flattened: any = {
                ...option,
            };
            // Extract nested values to top level
            if (option.value) {
                Object.keys(option.value).forEach((fieldName) => {
                    flattened[fieldName] = option.value?.[fieldName]?.current?.toString() || '';
                });
            }
            return flattened;
        });
    }, [calculationOptions]);

    const columnHeaderTemplate = (column: IWhitePaperColumn) => {
        return (
            <DefaultTooltipHostV9 appearance='inverted' withArrow positioning='after' content={t(column.entityName || '')}>
                {column.entityName?.toString()}
            </DefaultTooltipHostV9>
        );
    };

    // Template for editable columns - only show TextField if definition is INPUT
    const editableColumnTemplate = useCallback(
        (props: any, column: IWhitePaperColumn) => {
            const isInputType = props.definition === 'INPUT';
            const value = props[column.fieldName || ''] || '';

            //temp fix until the originalcolumnId is set properly for Elims columns
            //const headingText = column.headingFirstLine?.toLowerCase() || '';
            //const containsElim = headingText.includes('elim');
            const isNegativeIndex = (column.columnId ?? 0) < 0;
            const shouldBlockNegativeIndex = isNegativeIndex;
            // Show as plain text if: not INPUT type, negative index (unless header contains "elim"), or editing is not enabled
            if (!isInputType || !isEditingEnabled || shouldBlockNegativeIndex) {
                return <span>{value}</span>;
            }

            return (
                <EditableCell
                    initialValue={value}
                    onChange={(newValue) => handleChange(props.rowNumber || null, column.originalColumnId, newValue)}
                    ariaLabel={`${props.name} - ${column.entityName}`}
                    rowNumber={props.rowNumber || ''}
                    columnId={column.originalColumnId ?? 0}
                />
            );
        },
        [handleChange, isEditingEnabled]
    );

    if (!hasCalculationOptions) {
        return <div aria-label={t('noOptionsCanBeDisplayed').toString()}>{t('noOptionsCanBeDisplayed')}</div>;
    }

    return (
        <div style={containerStyles}>
            {isSearchEnabled && (
                <div style={searchContainerStyles}>
                    <SearchBox
                        value={searchValue}
                        className={searchBoxClasses.root}
                        placeholder={isSearchboxFocused ? '' : searchPlaceholder}
                        aria-label={searchPlaceholder}
                        onChange={(_ev, data) => handleSearch(data.value)}
                        onFocus={setIsSearchboxFocusedTrue}
                        onBlur={setIsSearchboxFocusedFalse}
                        dismiss={{ onClick: handleClearSearch }}
                    />
                </div>
            )}
            <div style={tableContainerStyles}>
                <style>{gridCustomStyles}</style>
                <StyledTable
                    ref={gridRef}
                    dataSource={flattenedData}
                    rowHeight={45}
                    height={'100%'}
                    data-testid='calculationOptionsTestId'
                    cssClass='calculation-options-grid'
                    allowSelection={false}
                    enableHover={false}
                    dataBound={handleDataBound}
                    searchSettings={{ ignoreCase: true, operator: 'contains', key: '' }}
                >
                    <ColumnsDirective>
                        <ColumnDirective
                            field='name'
                            headerTemplate={() => <label aria-label={t('optionName').toString()}>{t('name').toString()}</label>}
                            width='415'
                            minWidth='415'
                            isPrimaryKey={true}
                            freeze='Left'
                            allowEditing={false}
                        />
                        {columns?.map((column) =>
                            column.fieldName ? (
                                <ColumnDirective
                                    autoFit={true}
                                    maxWidth={columns.length > 1 ? '100%' : 'auto'}
                                    textAlign='Center'
                                    key={column.fieldName}
                                    field={column.fieldName}
                                    headerTemplate={() => columnHeaderTemplate(column)}
                                    template={(props: any) => editableColumnTemplate(props, column)}
                                    allowEditing={false}
                                />
                            ) : null
                        )}
                    </ColumnsDirective>
                    <Inject services={[Freeze]} />
                </StyledTable>
            </div>
        </div>
    );
};

export default CalculationOptionsTable;
